import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { BoardArtifactLink, BoardArtifacts } from '@nightshift/ui-types';

import { layoutBoxFromClientRect, layoutPx, placeAnchoredMenu } from '../lib/anchored-menu.ts';
import { artifactMeta, artifactUnavailable, artifactsButtonTitle } from '../lib/card-artifacts.ts';
import { uiScale, useTimeFormat } from '../lib/display.ts';
import { observedTime } from '../lib/format.ts';

/** Wide enough for a package name like `com.twopointzerostudios.cell-Signed.apk` on one line. */
const ARTIFACTS_MENU_WIDTH = 300;

/** A box with a lid: drawn in place, like every glyph outside the navigation. */
function ArchiveGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: 'block' }}>
      <path d="M2 3h12v3H2z" />
      <path d="M3 6v7.5h10V6" />
      <path d="M6.5 9h3" />
    </svg>
  );
}

function ArtifactRow({ link, now }: { link: BoardArtifactLink; now: number }) {
  const meta = artifactMeta(link, now);
  return (
    <li className={`tcard-artifact${link.url ? '' : ' is-unavailable'}${meta.expired ? ' is-expired' : ''}`}>
      {link.url ? (
        <a href={link.url} target="_blank" rel="noreferrer" draggable={false} onClick={(event) => event.stopPropagation()}>
          <span className="tcard-artifact-label">{link.label}</span>
          {link.fileName && link.fileName !== link.label ? <span className="mono tcard-artifact-file">{link.fileName}</span> : null}
        </a>
      ) : (
        <span>
          <span className="tcard-artifact-label">{link.label}</span>
          <span className="tcard-artifact-file dim">{artifactUnavailable(link)}</span>
        </span>
      )}
      {meta.text ? <small className="dim num">{meta.text}</small> : null}
      {link.browseUrl ? (
        <a className="tcard-artifact-browse dim" href={link.browseUrl} target="_blank" rel="noreferrer" draggable={false} onClick={(event) => event.stopPropagation()}>
          browse
        </a>
      ) : null}
    </li>
  );
}

/**
 * The card's bottom-right artifacts button and the small panel it opens.
 *
 * Key artifacts -- the APK, the build the project declared -- are listed first and plainly;
 * the rest of the pipeline's job archives sit behind one line saying how many there are. Every
 * link opens GitLab under the person's own login, which is the same page the merge request's
 * exposed-artifact section links to. Portaled and positioned like the ··· menu, for the same
 * reason: a board column clips anything absolutely positioned inside a card.
 */
export function CardArtifacts({ ticketKey, artifacts, now }: { ticketKey: string; artifacts: BoardArtifacts; now: number }) {
  const format = useTimeFormat();
  const [open, setOpen] = useState(false);
  const [showMore, setShowMore] = useState(artifacts.key.length === 0);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const button = buttonRef.current;
    if (!button) return;
    const scale = uiScale();
    setPos(placeAnchoredMenu({
      anchor: layoutBoxFromClientRect(button.getBoundingClientRect(), scale),
      menuWidth: panel?.offsetWidth ?? ARTIFACTS_MENU_WIDTH,
      menuHeight: panel?.offsetHeight ?? 120,
      viewportWidth: layoutPx(window.innerWidth, scale),
      viewportHeight: layoutPx(window.innerHeight, scale),
    }));
  }, [open, panel, showMore]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const count = artifacts.key.length + artifacts.more.length;
  const title = artifactsButtonTitle(artifacts);
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`tcard-artifacts-btn${open ? ' is-open' : ''}`}
        aria-label={`${ticketKey} artifacts: ${title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={title}
        draggable={false}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <ArchiveGlyph />
        <span className="num">{count}</span>
      </button>
      {open
        ? createPortal(
            <>
              <div
                className="tcard-menu-scrim"
                role="presentation"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                }}
              />
              <div
                ref={setPanel}
                className="menu tcard-artifacts"
                role="dialog"
                aria-label={`${ticketKey} artifacts`}
                style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden', top: 0, left: 0 }}
                onClick={(event) => event.stopPropagation()}
              >
                <header className="tcard-artifacts-head">
                  <strong>Artifacts</strong>
                  <a href={artifacts.pipelineUrl} target="_blank" rel="noreferrer" className="dim num" draggable={false}>
                    Pipeline #{artifacts.pipelineId} · {artifacts.sha.slice(0, 8)}
                  </a>
                </header>
                {artifacts.key.length > 0 ? (
                  <ul className="tcard-artifacts-list is-key">
                    {artifacts.key.map((link) => (
                      <ArtifactRow key={`${link.jobId}:${link.label}`} link={link} now={now} />
                    ))}
                  </ul>
                ) : null}
                {artifacts.more.length > 0 && artifacts.key.length > 0 ? (
                  <button
                    type="button"
                    className="tcard-artifacts-more"
                    aria-expanded={showMore}
                    onClick={() => setShowMore((current) => !current)}
                  >
                    {showMore ? 'Hide' : 'Show'} {artifacts.more.length} more job {artifacts.more.length === 1 ? 'archive' : 'archives'}
                  </button>
                ) : null}
                {artifacts.more.length > 0 && showMore ? (
                  <ul className="tcard-artifacts-list">
                    {artifacts.more.map((link) => (
                      <ArtifactRow key={link.jobId} link={link} now={now} />
                    ))}
                  </ul>
                ) : null}
                <footer className="tcard-artifacts-foot dim">
                  Observed {observedTime(artifacts.observedAt, now, format)}
                </footer>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
